import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Play, Quote } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../data/translations';

const TestimonialSection = () => {
  const { language } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials = [
    {
      id: 1,
      quote: t('testimonials.quote1', language),
      author: 'Martin Novák',
      role: 'HR Director',
      company: 'Volkswagen Slovakia',
      image: 'https://framerusercontent.com/images/UQWt4IBGGUgQv08Q9pKTp8zwnE.jpg',
      hasVideo: true
    },
    {
      id: 2,
      quote: t('testimonials.quote2', language),
      author: 'Jana Kováčová',
      role: 'Operations Manager',
      company: 'Amazon Logistics',
      image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=600&h=500&fit=crop',
      hasVideo: false
    }
  ];

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const current = testimonials[currentIndex];

  return (
    <section className="bg-[#FEF3C7] py-28 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#131132] mb-20 text-center"
        >
          {t('testimonials.title', language)}
        </motion.h2>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image/Video */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.5 }}
                className="relative aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl"
              >
                <img
                  src={current.image}
                  alt={current.author}
                  className="w-full h-full object-cover"
                />
                {current.hasVideo && (
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="absolute inset-0 flex items-center justify-center bg-black/20 group"
                  >
                    <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-2xl group-hover:bg-[#E8A238] transition-colors duration-300">
                      <Play className="w-10 h-10 text-[#131132] ml-2" fill="currentColor" />
                    </div>
                  </motion.button>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Navigation */}
            <div className="flex gap-4 mt-8 justify-center lg:justify-start">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={prevTestimonial}
                className="w-14 h-14 rounded-full border-2 border-[#131132] flex items-center justify-center hover:bg-[#131132] hover:text-white transition-all duration-300"
              >
                <ChevronLeft className="w-6 h-6" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={nextTestimonial}
                className="w-14 h-14 rounded-full border-2 border-[#131132] flex items-center justify-center hover:bg-[#131132] hover:text-white transition-all duration-300"
              >
                <ChevronRight className="w-6 h-6" />
              </motion.button>
            </div>
          </motion.div>

          {/* Quote */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="relative"
          >
            <Quote className="w-20 h-20 text-[#E8A238]/30 mb-6" />

            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
              >
                <blockquote className="text-2xl md:text-3xl lg:text-4xl font-medium text-[#131132] leading-snug mb-10">
                  "{current.quote}"
                </blockquote>

                <div>
                  <p className="text-2xl font-bold text-[#131132] mb-1">
                    {current.author}
                  </p>
                  <p className="text-lg text-[#131132]/60">
                    {current.role}
                  </p>
                  <p className="text-lg text-[#E8A238] font-medium">
                    {current.company}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Dots */}
            <div className="flex gap-3 mt-10">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`h-3 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? 'bg-[#E8A238] w-10'
                      : 'bg-[#131132]/20 w-3 hover:bg-[#131132]/40'
                  }`}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialSection;
